import type { ChangeEvent, FC } from "react"
import React, { useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  useTheme,
} from "@chakra-ui/react"
import type { AbiItem } from "web3-utils"
import type { FieldProps } from "formik"
import { Field, useFormikContext } from "formik"
import { isAddress } from "@ethersproject/address"
import invariant from "tiny-invariant"
import { DragDrop } from "@uppy/react"
import { useProvider } from "wagmi"
import isEqual from "lodash.isequal"

import CreateProposalFormError from "createProposal/components/CreateProposalFormError"
import { jsonParse } from "common/helpers/serialization"
import type CustomAction from "createProposal/action/CustomAction"
import CustomInput from "createProposal/components/actions/CustomInput"
import TupleInput from "createProposal/components/actions/TupleInput"
import {
  encodeAbiInput,
  filterAbiMethodsForProposal,
  getAbiTypes,
} from "createProposal/helpers/abi"
import { useContractData } from "createProposal/hooks/useContractData"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"
import { useUploadAbi } from "createProposal/hooks/useUploadAbi"
import type { CreateProposalFormValues } from "createProposal/types/form"
import type {
  AbiInputWithValue,
  AbiItemWithValues,
} from "createProposal/types/abi"
import { CREATE_PROPOSAL_ERRORS } from "errors/createProposal"
import WarningIcon from "ui/components/icons/WarningIcon"
import GreenCircleCheck from "ui/components/icons/GreenCircleCheck"
import { getChain } from "web3/helpers/chains"
import ValueArrayInput from "createProposal/components/actions/ValueArrayInput"
import WarningBanner from "common/components/banners/WarningBanner"
import ProposalCommand from "proposal/components/ProposalCommand"
import { getAddressBookContracts } from "createProposal/constants/address-book-contracts"
import { shortString } from "common/helpers/string"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import { chainIdToChainReference } from "web3/helpers/transformers"
import Link from "common/components/Link"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"
import { getAddressBookBridgeContracts } from "createProposal/constants/address-book-bridge-contracts"

type CreateProposalCustomRecipeProps = {
  action: CustomAction
  index: number
}

const CreateProposalCustomRecipe: FC<CreateProposalCustomRecipeProps> = ({
  action,
  index,
}) => {
  const {
    selectedAbiMethod,
    abiMethods,
    hasUploadedFile,
    isVerifiedOnBlockExplorer,
    value,
  } = action

  const targetAddress = action.getTarget()
  const abiTypes = getAbiTypes()

  // const [contractCode, setContractCode] = useState<string>()
  const [hasAddressMethods, setHasAddressMethods] = useState(false)
  const [didUserTriggerValue, setDidUserTriggerValue] = useState(
    Number(value) > 0,
  )
  const [selectedAbiType, setSelectedAbiType] = useState<string>("")

  const theme = useTheme()
  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()
  const provider = useProvider()
  const { data, isLoading, getContractData } = useContractData()
  const { governanceChainId, governanceId } = useCreateProposal()

  const chain = getChain(governanceChainId)
  const handleUploadedAbiMethods = (result: AbiItem[]) => {
    if (abiMethods) {
      const allAbiMethods = abiMethods.concat(result)

      setFieldValue(`actions.${index}.abiMethods`, allAbiMethods)
    } else {
      setFieldValue(`actions.${index}.abiMethods`, result)
    }

    if (result.length > 0) {
      setFieldValue(`actions.${index}.hasUploadedFile`, true)
    }
  }

  const { uppy } = useUploadAbi(handleUploadedAbiMethods)

  const isTargetAddressValid = isAddress(targetAddress)
  // const isContractAddress = contractCode && contractCode !== "0x"

  const hasAbiMethods = Boolean(abiMethods?.length)
  const isDisabledAbiMethodSelect = isLoading || !hasAbiMethods
  const abiMethodSelectPlaceholder = isLoading
    ? "Loading..."
    : "Select the contract method..."

  const isMethodNonPayable =
    selectedAbiMethod?.stateMutability === "nonpayable" ||
    selectedAbiMethod?.stateMutability === "pure" ||
    selectedAbiMethod?.stateMutability === "view"
  const shouldDisplayValueSwitch = isMethodNonPayable
  const shouldDisplayValueField =
    (selectedAbiMethod && !isMethodNonPayable) || didUserTriggerValue

  const hasSelectedStandardAbi =
    Boolean(selectedAbiType) && selectedAbiType !== "uploadAbi"

  useEffect(() => {
    if (!isTargetAddressValid || !provider) return

    // provider.getCode(targetAddress).then(setContractCode)

    if (abiMethods && abiMethods.length > 0) {
      setHasAddressMethods(true)
    } else {
      setHasAddressMethods(false)
    }
  }, [abiMethods, isTargetAddressValid, provider, targetAddress])
  useEffect(() => {
    const shouldFetchAbi = isTargetAddressValid //  && isContractAddress

    if (!shouldFetchAbi) return

    if (!action.abiMethods) {
      getContractData(targetAddress, governanceChainId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    targetAddress,
    isTargetAddressValid,
    // isContractAddress,
    governanceChainId,
  ])

  useEffect(() => {
    if (!data) return

    const { contractData, isContractVerified } = data

    if (!isContractVerified || !contractData) {
      setFieldValue(`actions.${index}.isVerifiedOnBlockExplorer`, false)

      if (!action.abiMethods) {
        setFieldValue(`actions.${index}.abiMethods`, undefined)
      }

      if (!action.selectedAbiMethod) {
        setFieldValue(`actions.${index}.selectedAbiMethod`, undefined)
      }

      return
    }

    const abiItems = jsonParse(contractData)
    const abiMethods = filterAbiMethodsForProposal(abiItems)

    setFieldValue(`actions.${index}.isVerifiedOnBlockExplorer`, true)
    setFieldValue(`actions.${index}.abiMethods`, abiMethods)
    // setFieldValue(`actions.${index}.selectedAbiMethod`, undefined) // To avoid cleaning the selectedAbiMethod when editing proposal
    if (!selectedAbiMethod) {
      setFieldValue(`actions.${index}.selectedAbiMethod`, undefined)
    }

    setSelectedAbiType("")

    if (abiMethods.length) {
      setHasAddressMethods(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleChangeSelectedAbiMethod = (e: ChangeEvent<HTMLSelectElement>) => {
    const { selectedIndex } = e.target

    const abiMethodIndex = selectedIndex - 1

    invariant(abiMethods, "abiMethods is not defined")

    const selectedAbiMethod = abiMethods[abiMethodIndex]

    if (!selectedAbiMethod) {
      setFieldValue(`actions.${index}.selectedAbiMethod`, undefined)

      return
    }

    const inputs: AbiInputWithValue[] = selectedAbiMethod.inputs
      ? selectedAbiMethod.inputs.map((abiInput) => ({
          ...abiInput,
          value: "",
        }))
      : []

    const selectedAbiMethodWithValues: AbiItemWithValues = {
      ...selectedAbiMethod,
      inputs,
    }

    setFieldValue(
      `actions.${index}.selectedAbiMethod`,
      selectedAbiMethodWithValues,
    )
  }

  const handleValidateTargetAddress = () => {
    if (isTargetAddressValid) return

    const targetAddressValidError =
      CREATE_PROPOSAL_ERRORS.actions.target.invalidAddressError()

    return targetAddressValidError.displayValue
  }

  const handleChangeSelectedAbiType = (e: ChangeEvent<HTMLSelectElement>) => {
    resetAbiState()
    setSelectedAbiType(e.target.value)

    const abiType = abiTypes.find((abiType) => abiType.name === e.target.value)

    if (abiType && abiType.abi) {
      const abiMethods = filterAbiMethodsForProposal(abiType.abi)
      setFieldValue(`actions.${index}.abiMethods`, abiMethods)
    } else if (e.target.value === "" && data) {
      const { contractData, isContractVerified } = data

      if (isContractVerified && contractData) {
        const abiItems = jsonParse(contractData)
        const abiMethods = filterAbiMethodsForProposal(abiItems)

        setFieldValue(`actions.${index}.isVerifiedOnBlockExplorer`, true)
        setFieldValue(`actions.${index}.abiMethods`, abiMethods)
      }
    }

    setFieldValue(`actions.${index}.selectedAbiMethod`, undefined)
  }

  const handleChangeValueSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target

    if (!checked) {
      setFieldValue(`actions.${index}.value`, 0)
    }

    setDidUserTriggerValue(checked)
  }

  const getAddressWarnings = () => {
    // if (!isContractAddress && !isVerifiedOnBlockExplorer && !hasAbiMethods) {
    if (!isVerifiedOnBlockExplorer && !hasAbiMethods) {
      return "Contract not found on Etherscan. Select an ABI or import a JSON file containing your ABI."
    }

    if (!isVerifiedOnBlockExplorer) {
      return "Contract not verified on Etherscan. Select an ABI or import a JSON file containing your ABI."
    }

    if (!hasAbiMethods) {
      return "Contract does not have any write methods. Select an ABI or import a JSON file containing your ABI."
    }

    // if (!hasAddressMethods && hasUploadedFile && !isContractAddress) {
    if (!hasAddressMethods && hasUploadedFile) {
      return "This may not be a contract address"
    }

    return null
  }

  const getAddressSuccess = () => {
    if (!hasAddressMethods) {
      return "Valid contract address"
    }

    return "Verified Contract found on Etherscan. ABI automatically imported."
  }

  const resetAbiState = () => {
    if (isTargetAddressValid) return

    setFieldValue(`actions.${index}.selectedAbiMethod`, undefined)
    setFieldValue(`actions.${index}.abiMethods`, undefined)
    setFieldValue(`actions.${index}.hasUploadedFile`, false)
    setFieldValue(`actions.${index}.isVerifiedOnBlockExplorer`, false)
  }

  const hashMethod = (abiMethod: AbiItem) => {
    if (!abiMethod) {
      return ""
    }

    const hash = `${abiMethod.name?.toLowerCase()}-${
      abiMethod?.inputs?.length ?? 0
    }`

    return hash
  }

  const addressBookContract = useMemo(() => {
    const contracts = getAddressBookContracts(governanceId)

    const bridgeContracts = getAddressBookBridgeContracts(governanceId)

    const allContracts = contracts?.concat(bridgeContracts ?? [])

    if (!allContracts) return undefined

    const _addressBookContract = allContracts.find((contract) =>
      isEqual(contract.address.toLowerCase(), targetAddress.toLowerCase()),
    )

    return _addressBookContract
  }, [governanceId, targetAddress])

  if (action.originalExecutableAction) {
    const executableCall = action.originalExecutableAction

    return (
      <Stack spacing={4}>
        <WarningBanner>
          <Text>{`This action can't be modified`}</Text>
        </WarningBanner>
        <ProposalCommand
          isDraftProposal
          calldata={executableCall.calldata || ""}
          chainId={governanceChainId}
          signature={executableCall.signature}
          target={executableCall.target}
          value={executableCall.value}
        />
      </Stack>
    )
  }

  return (
    <Stack spacing={4}>
      {addressBookContract ? (
        <Stack mb={4}>
          <Stack
            align="center"
            display="row"
            justify="center"
            spacing={2}
            width="100%"
          >
            <Text as="span" fontSize="lg" fontWeight="bold" mr={2}>
              {addressBookContract.name}
            </Text>
            <Text as="span" color="gray.600" fontWeight="regular" pr={2}>
              ({shortString(addressBookContract.address)})
            </Text>
            <Link
              isExternal
              href={EXTERNAL_ROUTES.etherscan.address(
                chainIdToChainReference(governanceChainId),
                targetAddress,
              )}
            >
              <Button size="xs" variant="tertiary">
                <Icon as={ArrowUpRight} h={3} w={3} />
              </Button>
            </Link>
          </Stack>
          {addressBookContract.description ? (
            <Text>{addressBookContract.description}</Text>
          ) : null}
        </Stack>
      ) : null}

      {addressBookContract ? (
        isLoading ? (
          <HStack>
            <Text color="gray.700">Loading...</Text>
          </HStack>
        ) : null
      ) : (
        <>
          <Field
            name={`actions.${index}.targetAddress`}
            validate={handleValidateTargetAddress}
          >
            {({ field, meta }: FieldProps) => {
              const { onChange, onBlur } = field
              const label = "Target contract address"

              const warningText = getAddressWarnings()

              const successText = getAddressSuccess()

              const text = warningText ?? successText

              const isInvalid = Boolean(meta.error && meta.touched)
              const shouldDisplayBadge = Boolean(!meta.error && meta.touched)
              const shouldDisplayWarningBadge = warningText !== null

              const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
                onChange(event)
                resetAbiState()
              }

              return (
                <FormControl isInvalid={isInvalid}>
                  <FormLabel fontWeight="semibold">{label}</FormLabel>
                  <Input
                    borderColor={shouldDisplayBadge ? "green.500" : "inherit"}
                    name={field.name}
                    placeholder="Enter the target address..."
                    value={targetAddress}
                    onBlur={onBlur}
                    onChange={handleChange}
                  />
                  {isLoading ? (
                    <FormHelperText>
                      <HStack>
                        <Text color="gray.700">Loading...</Text>
                      </HStack>
                    </FormHelperText>
                  ) : null}
                  {!isLoading && shouldDisplayBadge && (
                    <FormHelperText>
                      <HStack>
                        <Icon
                          as={
                            shouldDisplayWarningBadge
                              ? WarningIcon
                              : GreenCircleCheck
                          }
                          h={4}
                          w={4}
                        />
                        <Text color="gray.700">{text}</Text>
                      </HStack>
                    </FormHelperText>
                  )}
                  <CreateProposalFormError error={meta.error} />
                </FormControl>
              )
            }}
          </Field>
          {isTargetAddressValid ? (
            <FormControl pt={4}>
              <FormLabel fontWeight="semibold">
                {hasAddressMethods
                  ? "Use the imported ABI or upload yours"
                  : "Select an ABI or upload yours"}
              </FormLabel>
              <Select
                value={selectedAbiType}
                onChange={handleChangeSelectedAbiType}
              >
                <option value="">
                  {hasAddressMethods
                    ? "Use the imported ABI"
                    : "Select an option"}
                </option>
                {abiTypes.map((abiType, index) => (
                  <option key={`abi_type_${index}`} value={abiType.name}>
                    {abiType.name}
                  </option>
                ))}
                <option value="uploadAbi">Upload an ABI</option>
              </Select>
            </FormControl>
          ) : null}

          <Stack>
            {isTargetAddressValid && selectedAbiType === "uploadAbi" ? (
              <DragDrop
                height="7.5rem"
                locale={{
                  strings: {
                    dropHereOr: "Drag and drop your ABI file",
                  },
                }}
                note="Or click to browse your files"
                style={{
                  borderWidth: "1px",
                  borderColor: theme.colors.gray[100],
                }}
                uppy={uppy}
                width="100%"
                onDragLeave={(event) => event.clientY}
                onDragOver={(event) => event.clientX}
                onDrop={(event) => event}
              />
            ) : null}

            {hasUploadedFile && (
              <HStack isInline align="center" mt={0} pb={3}>
                <Icon as={GreenCircleCheck} h={3.5} w={3.5} />
                <Text color="gray.700" h="18.75px" textStyle="body.regular.md">
                  ABI file uploaded
                </Text>
              </HStack>
            )}
          </Stack>
        </>
      )}

      {isTargetAddressValid && hasAbiMethods ? (
        <FormControl>
          <FormLabel fontWeight="semibold">Contract method</FormLabel>

          <Select
            data-qa="contract-method-dropdown"
            disabled={isDisabledAbiMethodSelect}
            placeholder={abiMethodSelectPlaceholder}
            value={hashMethod(selectedAbiMethod as AbiItem)}
            onChange={handleChangeSelectedAbiMethod}
          >
            {abiMethods &&
              abiMethods.map((abiMethod, index) => (
                <option
                  key={`abi-method-${targetAddress}-${index}`}
                  data-qa={`item-${index + 1}-dropdown`}
                  value={hashMethod(abiMethod)}
                >
                  {abiMethod.name}
                </option>
              ))}
          </Select>
          {hasSelectedStandardAbi ? (
            <FormHelperText>
              <HStack>
                <Icon as={WarningIcon} h={4} w={4} />
                <Text color="gray.700">
                  This ABI is a standard. Please, be sure the smart contract
                  implements the method you selected.
                </Text>
              </HStack>
            </FormHelperText>
          ) : null}
        </FormControl>
      ) : null}
      <Box pt={3}>
        <Calldata
          index={index}
          inputs={selectedAbiMethod?.inputs}
          method={selectedAbiMethod}
        />
      </Box>

      <Stack spacing={4}>
        {shouldDisplayValueSwitch && (
          <FormControl display="flex">
            <FormLabel display="flex" mb={0}>
              <Text pt="px" textStyle="body.regular.sm">
                Also send {chain?.nativeCurrency?.name ?? ""} to the target
                address? (this is not common)
              </Text>
            </FormLabel>
            <Switch
              colorScheme="purple"
              isChecked={didUserTriggerValue}
              size="sm"
              onChange={handleChangeValueSwitch}
            />
          </FormControl>
        )}
        )
        {shouldDisplayValueField && (
          <>
            <Text textStyle="body.bold.lg">Value</Text>
            <Text>
              The amount of ETH you wish to send the target address (External
              Account or Smart Contract)
            </Text>
            <CustomInput
              label="ETH"
              name={`actions.${index}.value`}
              placeholder="Enter the value in ETH..."
            />
          </>
        )}
      </Stack>
    </Stack>
  )
}

export default CreateProposalCustomRecipe

type CalldataProps = {
  index: number
  inputs?: AbiInputWithValue[]
  method?: AbiItemWithValues
}

const Calldata: FC<CalldataProps> = ({ index, inputs, method }) => {
  if (!inputs || inputs.length < 1) return null

  return (
    <Stack spacing={4}>
      <Text textStyle="body.bold.lg">Calldatas</Text>
      <Text>
        The data for the function arguments you wish to send when the action
        executes
      </Text>
      {inputs?.map((abiInput, abiInputIndex) => {
        const placeholder =
          abiInput.type === "tuple"
            ? `tuple[${abiInput.components
                ?.map(({ type }) => type)
                .join(", ")}]`
            : abiInput.type

        const validate = () => {
          invariant(method, "method to validate is not defined")

          try {
            encodeAbiInput(abiInput)
          } catch (error) {
            const errorMessage =
              CREATE_PROPOSAL_ERRORS.actions.method.invalidArgs()

            return errorMessage.displayValue
          }

          return
        }

        const displayInput = () => {
          if (abiInput.type.endsWith("[]")) {
            return (
              <ValueArrayInput
                key={`method_${abiInput.name}_${abiInputIndex}`}
                components={
                  abiInput.components ? abiInput.components : undefined
                }
                label={abiInput.name}
                name={`actions.${index}.selectedAbiMethod.inputs.${abiInputIndex}.value`}
                placeholder={placeholder}
                type={abiInput.type}
                validate={validate}
              />
            )
          }

          if (abiInput.type === "tuple" && abiInput.components) {
            return (
              <TupleInput
                key={`method_${abiInput.name}_${abiInputIndex}`}
                components={abiInput.components}
                label={abiInput.name}
                name={`actions.${index}.selectedAbiMethod.inputs.${abiInputIndex}.value`}
                placeholder={placeholder}
                validate={validate}
              />
            )
          }

          return (
            <CustomInput
              key={`method_${abiInput.name}_${abiInputIndex}`}
              label={abiInput.name}
              name={`actions.${index}.selectedAbiMethod.inputs.${abiInputIndex}.value`}
              placeholder={placeholder}
              validate={validate}
            />
          )
        }

        return <>{displayInput()}</>
      })}
    </Stack>
  )
}
